import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { uniqBy } from 'lodash';
import React from 'react';

import './email-action-list.scss';
import { useTranslation } from '@hooks/index';
import { formatDateUTC_DEPRECATED } from '@utils/convertor';
import { showMaxLength } from '@utils/filter';

interface EmailActionListInterface {
    actions: any[];
    isCM?: boolean;
}

const EmailActionList = ({ actions, isCM }: EmailActionListInterface) => {
    const { t } = useTranslation();

    const mostRecent: SortDescriptor = { field: 'created', dir: 'desc' };

    const uniqueRisks = orderBy(
        uniqBy(
            actions.map((action: any) => action._risk),
            'id',
        ),
        [mostRecent],
    );

    const observationOrdering: SortDescriptor[] = isCM
        ? [{ field: '_siteName', dir: 'asc' }, mostRecent]
        : [mostRecent];
    const uniqueObservations = orderBy(
        uniqBy(
            actions.map((action: any) => action._observation),
            'id',
        ),
        observationOrdering,
    );

    const risksForEmail =
        uniqueRisks.map((risk: any) => {
            const currentObservation = uniqueObservations
                .filter((ob: any) => ob.riskId === risk?.id)
                .map((ob: any) => ({
                    ...ob,
                    _currentActions: orderBy(
                        actions.filter((action: any) => action.observationId === ob.id),
                        [mostRecent],
                    ),
                }));

            return {
                ...risk,
                _currentObservation: currentObservation,
            };
        }) ?? [];

    return (
        <div className="email-action-list">
            <div className="header">{t('ra:observationsActions')}</div>
            {risksForEmail.length
                ? risksForEmail.map((risk: any, riskIndex: number) => {
                      const isLastRisk = riskIndex === risksForEmail.length - 1;
                      return (
                          <div key={risk.id}>
                              {risk.recordId || risk.statement ? (
                                  <div
                                      className={`content ${
                                          isLastRisk && risk._currentObservation.length === 0 && 'last-element'
                                      }`}
                                  >
                                      <div className="title">
                                          {t('ra:riskId')}: {risk.recordId}
                                      </div>
                                      <span className="bold-text">{t('ra:statement')}: </span>
                                      {showMaxLength(risk.statement, 600)}
                                  </div>
                              ) : null}
                              {risk._currentObservation?.length
                                  ? risk._currentObservation.map((ob: any, obIndex: number) => {
                                        const isLastObservation = risk._currentObservation.length - 1 === obIndex;
                                        return (
                                            <div key={ob.id}>
                                                <div
                                                    className={`content ${
                                                        ob._currentActions?.length === 0 &&
                                                        isLastRisk &&
                                                        isLastObservation &&
                                                        'last-element'
                                                    }`}
                                                >
                                                    <div className="title">
                                                        {t('ra:observationId')}: {ob.recordId}
                                                    </div>
                                                    <br />
                                                    {isCM ? (
                                                        <>
                                                            <span className="bold-text">
                                                                {t('cm:site')}:<span>{ob._siteName || ob._site}</span>
                                                            </span>
                                                            <span className="bold-text">
                                                                {t('cm:panelName')}:<span>{ob._panelName}</span>
                                                            </span>
                                                            <span className="bold-text">
                                                                {t('cm:datasetDate')}:
                                                                <span>
                                                                    {ob.dataSet
                                                                        ? formatDateUTC_DEPRECATED(
                                                                              ob.dataSet?.dataCutDate,
                                                                              true,
                                                                              'DD-MMM-YYYY',
                                                                          )
                                                                        : ''}
                                                                </span>
                                                            </span>
                                                        </>
                                                    ) : null}
                                                    <span className="bold-text">
                                                        {t('created')} {t('by')}:{' '}
                                                        <span>
                                                            {ob.createdBy
                                                                ? `${ob.createdBy.firstName} ${ob.createdBy.lastName}`
                                                                : null}
                                                        </span>
                                                    </span>
                                                    <br />
                                                    <span className="bold-text">
                                                        {t('ra:observation')}:
                                                        <span>{showMaxLength(ob.description, 1200)}</span>
                                                    </span>
                                                    {isCM ? (
                                                        <>
                                                            <br />
                                                            <span className="bold-text">
                                                                {t('ra:potentialCause')}:
                                                                <span>{showMaxLength(ob.potentialCause, 1200)}</span>
                                                            </span>
                                                            <br />
                                                            <span className="bold-text">
                                                                {t('ra:confirmedCause')}:
                                                                <span>{showMaxLength(ob.confirmedCause, 1200)}</span>
                                                            </span>
                                                            <br />
                                                            <span className="bold-text mr-10">
                                                                {t('ra:observationDate')}:
                                                                <span>
                                                                    {ob.observationDate
                                                                        ? formatDateUTC_DEPRECATED(
                                                                              ob.observationDate,
                                                                              true,
                                                                              'DD-MMM-YYYY',
                                                                          )
                                                                        : ''}
                                                                </span>
                                                            </span>
                                                            <span className="bold-text">
                                                                {t('ra:closedDate')}:
                                                                <span>
                                                                    {ob.observationDate
                                                                        ? formatDateUTC_DEPRECATED(
                                                                              ob.closedDate,
                                                                              true,
                                                                              'DD-MMM-YYYY',
                                                                          )
                                                                        : ''}
                                                                </span>
                                                            </span>
                                                        </>
                                                    ) : null}
                                                </div>
                                                <br />
                                                {ob._currentActions?.length
                                                    ? ob._currentActions.map((action: any, actionsIndex: number) => {
                                                          const isLastAction =
                                                              ob._currentActions.length - 1 === actionsIndex;
                                                          return (
                                                              <div
                                                                  key={action.id}
                                                                  className={`content 
                                                    ${isLastAction && 'element-with-delimiter'} 
                                                    ${isLastAction && isLastRisk && 'last-element'}`}
                                                              >
                                                                  <div className="title">
                                                                      {t('ra:actionId')}: {action.recordId}
                                                                  </div>
                                                                  <br />
                                                                  <span className="bold-text">
                                                                      {t('description')}:
                                                                      <span>
                                                                          {showMaxLength(action.description, 1200)}
                                                                      </span>
                                                                  </span>
                                                                  <br />
                                                                  {isCM ? (
                                                                      <>
                                                                          <span className="bold-text">
                                                                              {t('cm:actionTaken')}:
                                                                              <span>
                                                                                  {showMaxLength(
                                                                                      action.actionTaken,
                                                                                      1200,
                                                                                  )}
                                                                              </span>
                                                                          </span>
                                                                          <br />
                                                                          <span className="bold-text">
                                                                              {t('cm:rootCause')}:
                                                                              <span>
                                                                                  {showMaxLength(
                                                                                      action.rootCause,
                                                                                      1200,
                                                                                  )}
                                                                              </span>
                                                                          </span>
                                                                          <br />
                                                                          {action.requestDate ? (
                                                                              <span className="bold-text">
                                                                                  {t('ra:requestedDate')}:
                                                                                  <span>
                                                                                      {formatDateUTC_DEPRECATED(
                                                                                          action.requestDate,
                                                                                          true,
                                                                                          'DD-MMM-YYYY',
                                                                                      )}
                                                                                  </span>
                                                                              </span>
                                                                          ) : null}
                                                                          {action._owner ? (
                                                                              <span className="bold-text">
                                                                                  {t('ra:owner')}:
                                                                                  <span>
                                                                                      {action._owner?.text ?? ''}
                                                                                  </span>
                                                                              </span>
                                                                          ) : null}
                                                                          {action.expectedCompleteDate ? (
                                                                              <span className="bold-text">
                                                                                  {t('ra:expectedCompletionDate')}:
                                                                                  <span>
                                                                                      {formatDateUTC_DEPRECATED(
                                                                                          action.expectedCompleteDate,
                                                                                          true,
                                                                                          'DD-MMM-YYYY',
                                                                                      )}
                                                                                  </span>
                                                                              </span>
                                                                          ) : null}
                                                                      </>
                                                                  ) : null}
                                                              </div>
                                                          );
                                                      })
                                                    : null}
                                            </div>
                                        );
                                    })
                                  : null}
                          </div>
                      );
                  })
                : null}
        </div>
    );
};

export default EmailActionList;
